import { i18n } from "@lingui/core";

import { loadProviderStrings } from "@/components/provider/strings";
import { readCookie, writeCookie } from "@/lib/cookie";

/**
 * 8 mandatory locales (PontiMed contract): en, ro, bg, de, fr, hu, it, el.
 * 7 secondary (English fallback until translated): hr, sr, cs, es, pl, tr, uk.
 *
 * ALL locales lazy-load on demand, including en + ro. Eager-importing the
 * canonical pair previously added ~300 KB to the main bundle for every user
 * even though each visit only renders one locale. Boot now dynamic-imports
 * only the detected locale before mounting React; `<RouterProvider />` is
 * not rendered until the active catalog is loaded, so we never flash msgid
 * fallbacks.
 */
export const SUPPORTED_LOCALES = [
  "en",
  "ro",
  "bg",
  "de",
  "fr",
  "hu",
  "it",
  "el",
  "hr",
  "sr",
  "cs",
  "es",
  "pl",
  "tr",
  "uk",
] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const PRIMARY_LOCALES: SupportedLocale[] = ["en", "ro", "bg", "de", "fr", "hu", "it", "el"];

export const DEFAULT_LOCALE: SupportedLocale = "en";

const LOCALE_COOKIE = "pontimed_locale";
const LOCALE_STORAGE_KEY = "pontimed.locale";

export function isSupportedLocale(value: string): value is SupportedLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function detectInitialLocale(): SupportedLocale {
  if (typeof window === "undefined") return DEFAULT_LOCALE;

  const fromCookie = readCookie(LOCALE_COOKIE);
  if (fromCookie && isSupportedLocale(fromCookie)) return fromCookie;

  const fromStorage = window.localStorage?.getItem(LOCALE_STORAGE_KEY);
  if (fromStorage && isSupportedLocale(fromStorage)) return fromStorage;

  // `?lang=` on a notification link (email, push): a device that has never
  // opened the portal starts in the language the message was written in.
  // It never overrides a language the device already has, so a link in the
  // profile's language can't undo a switch made in the header.
  // hydrateInitialLocale persists it.
  const fromUrl = new URLSearchParams(window.location.search).get("lang")?.toLowerCase();
  if (fromUrl && isSupportedLocale(fromUrl)) return fromUrl;

  for (const candidate of navigator.languages ?? [navigator.language]) {
    const short = candidate.toLowerCase().split("-")[0];
    if (isSupportedLocale(short)) return short;
  }

  return DEFAULT_LOCALE;
}

const loadedLocales = new Set<SupportedLocale>();

export async function loadLocale(locale: SupportedLocale): Promise<void> {
  if (loadedLocales.has(locale)) return;
  // The provider-ui dictionaries (profile sheet, cards) travel with the catalog.
  const [mod] = await Promise.all([import(`@/locales/${locale}.ts`), loadProviderStrings(locale)]);
  i18n.load(locale, mod.messages);
  loadedLocales.add(locale);
}

export async function setLocale(locale: SupportedLocale): Promise<void> {
  await loadLocale(locale);
  i18n.activate(locale);
  writeCookie(LOCALE_COOKIE, locale);
  if (typeof window !== "undefined") {
    window.localStorage?.setItem(LOCALE_STORAGE_KEY, locale);
    document.documentElement.lang = locale;
  }
}

/** Boot-time hydration. Call once in main.tsx before <RouterProvider />. */
export async function hydrateInitialLocale(): Promise<SupportedLocale> {
  const detected = detectInitialLocale();
  await setLocale(detected);
  return detected;
}

export { i18n };
